import { isElementInViewport, isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

class StickyAddToCart extends HTMLElement {
  #variantPickerSlot = null;
  #mainVariantPicker = null;
  #variantOptionsMirror = null;
  #onVariantOptionsInteraction = this.#handleVariantOptionsInteraction.bind(this);
  #onVariantUpdated = this.#handleVariantUpdated.bind(this);
  #onSubscriptionUpdated = this.#syncAddToCartLabel.bind(this);
  #onBreakpointChange = this.#handleBreakpointChange.bind(this);
  #onScroll = this.#displayStickyAddToCart.bind(this);

  constructor() {
    super();

    this.form = null;
    this.footer = null;
  }

  connectedCallback() {
    this.form = this.closest('[data-ref="product-form"]');
    this.footer = document.querySelector('[data-ref="footer"]');
    this.#variantPickerSlot = this.querySelector('[data-ref="sticky-variant-picker"]');

    this.#setupMobileVariantOptions();
    this.#displayStickyAddToCart();

    window.addEventListener('scroll', this.#onScroll);
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
    this.form?.addEventListener(ThemeEvents.variantUpdated, this.#onVariantUpdated);
    this.form?.addEventListener(ThemeEvents.subscriptionUpdated, this.#onSubscriptionUpdated);
    this.form?.addEventListener(ThemeEvents.formUpdated, this.#onSubscriptionUpdated);
    this.#syncAddToCartLabel();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
    this.form?.removeEventListener(ThemeEvents.variantUpdated, this.#onVariantUpdated);
    this.form?.removeEventListener(ThemeEvents.subscriptionUpdated, this.#onSubscriptionUpdated);
    this.form?.removeEventListener(ThemeEvents.formUpdated, this.#onSubscriptionUpdated);
    this.#variantOptionsMirror?.removeEventListener('click', this.#onVariantOptionsInteraction);
    this.#variantOptionsMirror?.removeEventListener('change', this.#onVariantOptionsInteraction);
  }

  #shouldUseStickyBehavior() {
    const mode = this.dataset.stickyShowOn || 'desktop_and_mobile';

    if (mode === 'desktop_only') return !isMobileBreakpoint();
    if (mode === 'mobile_only') return isMobileBreakpoint();

    return true;
  }

  #displayStickyAddToCart() {
    if (!this.#shouldUseStickyBehavior()) {
      this.dataset.active = 'false';
      this.classList.remove('color-scheme-1');
      return;
    }

    const formIsBelowScroll = this.form && (this.form.getBoundingClientRect().top > window.innerHeight);
    if (isElementInViewport(this.form) || isElementInViewport(this.footer) || formIsBelowScroll) {
      this.dataset.active = 'false';
      this.classList.remove('color-scheme-1');
    } else {
      this.dataset.active = 'true';
      this.classList.add('color-scheme-1');
    }
  }

  #getMainVariantPicker() {
    if (!this.form) return null;

    const pickers = [...this.form.querySelectorAll('variant-picker')].filter(
      (picker) => !this.contains(picker) && !picker.closest('[data-ref="quantity-breaks"]'),
    );

    return pickers[0] ?? null;
  }

  #handleBreakpointChange() {
    this.#setupMobileVariantOptions();
  }

  #handleVariantUpdated() {
    requestAnimationFrame(() => {
      this.#setupMobileVariantOptions();
      this.#syncAddToCartLabel();
    });
  }

  /* AB-TEST: recopier le picker une fois les variantes masquées. À retirer en fin de test. */
  refreshVariantOptions() {
    this.#setupMobileVariantOptions();
  }

  #setupMobileVariantOptions() {
    if (!this.#variantPickerSlot || this.dataset.showVariantPicker !== 'true') return;

    if (!isMobileBreakpoint()) {
      this.#variantPickerSlot.replaceChildren();
      this.#variantOptionsMirror = null;
      return;
    }

    this.#mainVariantPicker = this.#getMainVariantPicker();
    if (!this.#mainVariantPicker) {
      this.#variantPickerSlot.replaceChildren();
      return;
    }

    this.#mountMobileVariantOptions();
  }

  #mountMobileVariantOptions() {
    if (!this.#variantPickerSlot || !this.#mainVariantPicker) return;

    this.#variantOptionsMirror?.removeEventListener('click', this.#onVariantOptionsInteraction);
    this.#variantOptionsMirror?.removeEventListener('change', this.#onVariantOptionsInteraction);

    const mirror = document.createElement('div');
    mirror.className = 'sticky-add-to-cart__variant-options';

    for (const optionValues of this.#mainVariantPicker.querySelectorAll('.variant-picker__option-values')) {
      const optionValuesMirror = document.createElement('div');
      optionValuesMirror.className = 'variant-picker__option-values';
      optionValuesMirror.innerHTML = optionValues.innerHTML;
      this.#sanitizeMirror(optionValuesMirror);
      mirror.appendChild(optionValuesMirror);
    }

    if (!mirror.children.length) {
      this.#variantPickerSlot.replaceChildren();
      return;
    }

    this.#variantPickerSlot.replaceChildren(mirror);
    this.#variantOptionsMirror = mirror;
    this.#variantOptionsMirror.addEventListener('click', this.#onVariantOptionsInteraction);
    this.#variantOptionsMirror.addEventListener('change', this.#onVariantOptionsInteraction);
  }

  #sanitizeMirror(root) {
    for (const element of root.querySelectorAll('[name]')) {
      element.removeAttribute('name');
    }

    for (const element of root.querySelectorAll('[id]')) {
      element.id = `sticky-${element.id}`;
    }

    for (const element of root.querySelectorAll('label[for]')) {
      const labelFor = element.getAttribute('for');
      if (labelFor) element.setAttribute('for', `sticky-${labelFor}`);
    }
  }

  #syncAddToCartLabel() {
    const subscription = this.form?.querySelector('[data-ref="subscription"]');
    const isSubscription = subscription?.isSubscriptionSelected?.() === true;

    for (const label of this.querySelectorAll('[data-ref="add-to-cart-label"]')) {
      const oneTimeLabel = label.querySelector('[data-ref="add-to-cart-label-one-time"]');
      const subscriptionLabel = label.querySelector('[data-ref="add-to-cart-label-subscription"]');

      if (!oneTimeLabel || !subscriptionLabel) continue;

      oneTimeLabel.hidden = isSubscription;
      subscriptionLabel.hidden = !isSubscription;
      label.classList.toggle('add-to-cart-label--subscription', isSubscription);
    }
  }

  #handleVariantOptionsInteraction(event) {
    if (!(event.target instanceof HTMLElement) || !this.#mainVariantPicker) return;

    const optionTarget = event.target.closest('[data-option-value-id]');
    if (!(optionTarget instanceof HTMLElement)) return;

    const optionValueId = optionTarget.dataset.optionValueId;
    if (!optionValueId) return;

    const mainTarget = this.#mainVariantPicker.querySelector(`[data-option-value-id="${optionValueId}"]`);
    if (!mainTarget) return;

    if (mainTarget instanceof HTMLInputElement) {
      mainTarget.checked = true;
      mainTarget.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (mainTarget instanceof HTMLOptionElement && mainTarget.parentElement instanceof HTMLSelectElement) {
      mainTarget.parentElement.value = mainTarget.value;
      mainTarget.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
